import { Box, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate } from 'react-router-dom';

import femaleAudioFile from '../../../assets/female_sample_voice.wav';
import maleAudioFile from '../../../assets/male_sample_voice.wav';
import StyledFilters from '../../../components/dashboard/MultiAgentDashboard/StyledFilters';
import AgentProfileAvatar from '../../../components/shared/AgentProfileAvatar/AgentProfileAvatar';
import AutocompleteInput from '../../../components/shared/AutoCompleteInput/AutoCompleteInput';
import BarChart from '../../../components/shared/BarChart/BarChart';
import Button from '../../../components/shared/Button/Button';
import ConversationsTable from '../../../components/shared/ConversationsTable/ConversationsTable';
import DateRangePicker from '../../../components/shared/DateRangePicker/DateRangePicker';
import Input from '../../../components/shared/Input/Input';
import LiveSyncStatus from '../../../components/shared/LiveSyncToggle/LiveSyncStatus';
import LiveSyncStoppedModal from '../../../components/shared/LiveSyncToggle/LiveSyncStoppedModal';
import LiveSyncToggle from '../../../components/shared/LiveSyncToggle/LiveSyncToggle';
import Loading from '../../../components/shared/Loading/Loading';
import OverAllMetrics from '../../../components/shared/OverAllMetrics/OverAllMetrics';
import Page from '../../../components/shared/Page/Page';
import SecondaryFooter from '../../../components/shared/SecondaryFooter/SecondaryFooter';
import TelephoneInput from '../../../components/shared/TelephoneInput/TelephoneInput';
import Text from '../../../components/shared/Text/Text';
import ToolTip from '../../../components/shared/ToolTip/ToolTip';
import {
  removeWhiteSpaces,
  snakeCaseToCapitalized,
} from '../../../helpers/agentHelper';
import { useSingleAgentDashboard } from '../../../hooks/queries/useSingleAgentDashboard';
import useGetAgentById from '../../../hooks/useGetAgentById';
import useAuth from '../../../hooks/useAuth';
import { useErrorToast } from '../../../hooks/useErrorToast';
import { useLiveSync } from '../../../hooks/useLiveSync';
import { useIsFeatureEnabled } from '../../../hooks/useIsFeatureEnabled';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentCapability, AgentType } from '../../../interfaces/Agent';
import { FeatureGate } from '../../../interfaces/FeatureGate';
import { updateAgent } from '../../../services/agentService';
import HorizontalStack from '../../../styles/HorizontalStack';
import StyledForm from '../../../styles/StyledForm';
import StyledFormItem from '../../../styles/StyledFormItem';
import ExternalCapabilities from './ExternalCapabilities';

type FormValues = {
  custom_phone_number?: string;
  data_to_collect: string[];
  default_sentence: string;
  opening_sentence: string;
  system_phone_number?: string;
};

const ShowAgent: React.FC = () => {
  const methods = useForm<FormValues>();
  const { agent, isLoading, shouldNavigate } = useGetAgentById();
  const { currentUser } = useAuth();
  const { t } = useTranslation();

  const {
    callsPerMonth,
    callsPerDay,
    conversations,
    dateRange,
    agentsOverAllMetrics,
    setDateRange,
    serverPaging,
    isRefreshingMetrics,
    refetch: refetchDashboard,
    isLoading: isLoadingDashboard,
    isFetching: isFetchingDashboard,
    error: dashboardError,
  } = useSingleAgentDashboard(
    agent?.company_id ?? currentUser?.company_id,
    agent?.user_id ?? currentUser?.id,
    agent?.id
  );
  const liveSync = useLiveSync({
    refetch: refetchDashboard,
    isUpdating: isFetchingDashboard,
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const displayMessage = useSnackBar();
  useErrorToast(dashboardError);

  const isBookingCapabilityEnabled = useIsFeatureEnabled(
    FeatureGate.BOOKING_CAPABILITY
  );

  const externalCapabilities = isBookingCapabilityEnabled
    ? Object.entries(agent?.external_capabilities ?? {})
        .filter(([_, value]) => Array.isArray(value) && value.length > 0)
        .map(([key, _]) => snakeCaseToCapitalized(key))
    : Object.keys(agent?.external_capabilities ?? {})
        .filter(key => key == AgentCapability.TRANSFER_CALLS) // transfer_calls only
        .map(key => snakeCaseToCapitalized(key));

  const handlePlayDemo = () => {
    setIsPlaying(!isPlaying);
  };

  const updateAgentDetails = async (agentData: FormValues) => {
    const cleanedPhone = removeWhiteSpaces(agentData.custom_phone_number ?? '');
    const originalPhone = removeWhiteSpaces(agent?.custom_phone_number ?? '');

    // If not changed, remove it from payload
    if (cleanedPhone === originalPhone || cleanedPhone === '') {
      delete agentData.custom_phone_number;
    } else {
      agentData.custom_phone_number = cleanedPhone;
    }

    delete agentData.system_phone_number; // still never submit this

    try {
      await updateAgent(agent?.id ?? '', agentData);
      displayMessage(t('Agent details were updated'), 'success');

      // Refresh the form fields after successful update
      methods.reset({
        ...methods.getValues(),
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err.message);
        displayMessage(err.message, 'error');
      }
    }
  };

  useEffect(() => {
    if (!agent) return;

    const {
      custom_phone_number,
      data_to_collect,
      default_sentence,
      opening_sentence,
      system_phone_number,
    } = agent;

    methods.reset({
      custom_phone_number,
      data_to_collect,
      default_sentence,
      opening_sentence,
      system_phone_number,
    });
  }, [agent, methods]);

  if (shouldNavigate) {
    return <Navigate to="/dashboard" />;
  }

  if (isLoading || !agent) return <Loading />;

  return (
    <Page>
      <HorizontalStack gap="1.5rem" alignItems="flex-start">
        {/* Left column */}
        <Stack direction="column" width={{ xs: '100%', md: '32%' }} gap={2}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 2 },
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
            }}
          >
            <AgentProfileAvatar
              agentId={agent.id}
              aiAgentImage={agent.avatar}
              aiAgentVoice={
                agent.voice === 'female' ? femaleAudioFile : maleAudioFile
              }
              agentName={agent.name}
              agentStatus={agent.status}
              isPlaying={isPlaying}
              playAgentVoice={handlePlayDemo}
              voice={agent.voice}
              showTestButton={true}
            />
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 2 },
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Typography component="h2" variant="h6" sx={{ mb: 1 }}>
              {t('Agent settings')}
            </Typography>
            <FormProvider {...methods}>
              <StyledForm
                gap="unset"
                marginTop="0"
                onSubmit={methods.handleSubmit(updateAgentDetails)}
              >
                <StyledFormItem>
                  <Text type="label">
                    {t("Agent response if it doesn't know the answer")}
                    <ToolTip
                      title={t(
                        'The prompt message of the agent if and only if it does not know the answer.'
                      )}
                    />
                  </Text>
                  <Input name="default_sentence" required={true} />
                </StyledFormItem>

                <StyledFormItem>
                  <Text type="label">
                    {t('Data to collect')}{' '}
                    <ToolTip
                      title={t(
                        'The data that you allow us to collect and store based on our privacy policy.'
                      )}
                    />
                  </Text>
                  <AutocompleteInput
                    name="data_to_collect"
                    options={['name', 'email', 'company']}
                    initialValue={[]}
                  />
                </StyledFormItem>

                {externalCapabilities.length > 0 && (
                  <StyledFormItem>
                    <Text type="label">
                      {t('Capabilities')}
                      <ToolTip
                        title={t('The list of your agent capabilities.')}
                      />
                    </Text>
                    <ExternalCapabilities capabilities={externalCapabilities} />
                  </StyledFormItem>
                )}

                {agent?.custom_phone_number !== '' && (
                  <TelephoneInput
                    name="custom_phone_number"
                    disabled={
                      !agent?.custom_phone_number &&
                      agent?.custom_phone_number === agent?.system_phone_number
                    }
                  />
                )}

                <Button type="submit" disabled={!methods.formState.isDirty}>
                  {t('Save')}
                </Button>
              </StyledForm>
            </FormProvider>
          </Paper>
        </Stack>
        {/* Right column */}
        <Stack width={{ xs: '100%', md: '68%' }} direction="column">
          <StyledFilters>
            <DateRangePicker
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              onChange={setDateRange}
            />
            <LiveSyncToggle
              enabled={liveSync.isEnabled}
              onChange={liveSync.setIsEnabled}
              disabled={isLoadingDashboard}
            />
          </StyledFilters>
          {liveSync.isEnabled && (
            <LiveSyncStatus
              isUpdating={liveSync.isUpdating}
              secondsSinceUpdate={liveSync.secondsSinceUpdate}
            />
          )}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 2 },
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
            }}
          >
            <OverAllMetrics
              {...agentsOverAllMetrics}
              isRefreshing={isRefreshingMetrics}
            />
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 2 },
              mt: 2,
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
            }}
          >
            <BarChart
              agentType={AgentType.CALL}
              callsPerMonth={callsPerMonth}
              perDay={callsPerDay}
              dateRange={dateRange}
            />
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 2 },
              mt: 2,
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography component="h3" variant="h6" sx={{ m: 0 }}>
                {t('Conversations')}
              </Typography>
            </Box>
            <ConversationsTable
              conversations={conversations}
              serverPaging={serverPaging}
            />
          </Paper>
        </Stack>
      </HorizontalStack>
      <LiveSyncStoppedModal
        open={liveSync.showLimitModal}
        setOpen={liveSync.setShowLimitModal}
      />
      <SecondaryFooter />
    </Page>
  );
};

export default ShowAgent;
